import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ReactComponent as TruckIcon } from "assets/svgs/truck_icon.svg";
import SaveToCalendar from "components/save-to-calendar";
import obtainOrderInfo from "utils/obtain-order-info";
import formatDate, { getDayName, getAdjustedDate } from "helpers/format-date";
import { useGlobalContext } from "context/global";
import SkipDeliveries from "components/skip-deliveries";

//When the input includes a string enclosed in {}, it eliminates the {} and changes the color of that text to green.
const formatString = (input) => {
	const parts = input.split(/{([^}]*)}/g);
	const formattedParts = parts.map((part, index) => {
		if (index % 2 === 1)
			return <span key={index} className="text-green-discount">{part}</span>;
		else
			return part;
	});

	return formattedParts;
};

const UpcomingDelivery = () => {
	const { translation, langCode } = useGlobalContext();
	const { nextBillingDate, chargeOffsetDays, isActive, shippingAddress, deliveryDate } =
		useSelector((state) => state.account.subscription);
	const { latestSubOrder, defaultAddress } = useSelector(state => state.account.profile);

	const skipModalRef = useRef(null);

	if (!isActive) return <></>;
	const { date, isDispatched, isTrackable } = obtainOrderInfo(nextBillingDate, chargeOffsetDays, latestSubOrder, deliveryDate);
	const handleOpenSkipModal = () => {
		if (isDispatched) {
			toast.error(translation.subscription?.order_in_transit || "This order is already in transit and cannot be changed.");
			return;
		}
		skipModalRef.current?.();
	};

	const NextOrderDate = () => {
		// Month Date, Year
		const formattedDate = formatDate(date, langCode, { year: "numeric", month: "short", day: "numeric" });
		const [day, month, year] = formattedDate.split(" ");
		const dateString = `${getDayName(date, langCode)}, ${month} ${day.replace(".", "")} ${year}`;

		const deliveryCountry = shippingAddress?.countryCode || defaultAddress?.country;
		if (isDispatched || !deliveryCountry)
			return dateString;

		return <button className="text-h3-xs md:text-h3 underline" onClick={handleOpenSkipModal}>
			{dateString}
		</button>;
	};

	return (
		<div className="flex md:flex-row flex-col w-full justify-between md:p-10 px-4 pt-4 pb-8 md:p4 lg:w-90 mx-auto">
			<div className="flex flex-col">
				<div className="w-full">
					<h3 className="flex items-center flex-wrap gap-[10px]">
						{translation.subscription?.upcoming_delivery}{" "}
						<span className="date-edit-group">
							<NextOrderDate />
							<button className="edit-button shrink-0" onClick={handleOpenSkipModal}>
								{translation.account?.edit}
							</button>
						</span>
					</h3>
				</div>
				<p className="text-secondary pt-2">
					{isDispatched ? translation.subscription?.dispatched_order :
						<>
							{formatString(translation.subscription?.undispatched_order).map((part, index) => (
								<React.Fragment key={index}>{part} </React.Fragment>
							))}
							{(() => {
								// Monday DD: charge is 3 days before delivery (Fri); last edit is Thu (delivery - 4).
								const isMondayDelivery = new Date(date).getDay() === 1;
								const adjusted = isMondayDelivery
									? getAdjustedDate(date, -4)
									: getAdjustedDate(nextBillingDate, -1);
								return `${getDayName(adjusted, langCode)}, ${formatDate(adjusted, langCode)}`;
							})()}
						</>
					}
				</p>
			</div>
			<div className="cursor-pointer my-2 md:my-0">
				{isTrackable && latestSubOrder?.fulfillments?.length > 0 &&
					<a target="_BLANK" className="header-icon order-icon"
						href={latestSubOrder?.fulfillments[0]?.trackingInfo[0]?.url} rel="noreferrer"
						aria-label={`${translation.subscription?.track_delivery} (opens in new tab)`}
					>
						<TruckIcon /> {translation.subscription?.track_delivery}
					</a>}
			</div>

			{defaultAddress?.address1 && (
				<SaveToCalendar
					title={translation.account.save_to_calendar_title}
					label={translation.account.save_to_calendar_label}
					start={new Date(date).setHours(9, 0, 0, 0)}
					location={`${defaultAddress.address1}, ${defaultAddress?.country}`}
					end={new Date(date).setHours(18, 0, 0, 0)}
				/>
			)}
			<SkipDeliveries
				renderTrigger={({ openModal }) => {
					skipModalRef.current = openModal;
					return null;
				}}
				hideTrigger
				flow="edit"
			/>
		</div>
	);
};

export default UpcomingDelivery;
